import { Button, createStyles, SimpleGrid } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";

import { NoBlockedDates } from "./NoBlockedDates";
import CreateNewBlockedDates from "./CreateNewBlockedDates";
import { OdpBlockedDatesContext } from "../../providers/BlockedDatesProvider";
import {
  IBlockedDateEvent,
  IGroupedBlockedDateEvents,
  OdpBlockedDateContextType,
} from "../../@types/blockedDates";
import EditBlockedDateDrawer from "./EditBlockedDates";
import ConfirmationBlockedDatesModal from "../Modals/ConfirmationBlockedDatesModal";
import DeleteBlockedDatesModal from "../Modals/DeleteBlockedDatesModal";
import ProgressIndicator from "../../../../themes/components/ProgressIndicator";
import BlockedDatesOverView from "./BlockedDatesOverView";
import BlockDatesFilter, { ItemProps } from "./BlockDatesFilter";
import EditIndividualBlockDateEmployees from "./EditIndividualBlockDateEmployees";
import { splitRecurringEvents } from "../../utils/OdpLimitsUtils";
import BlockedDatesHistoryLog from "./BlockedDatesHistoryLog";

const colors = {
  companyWide: {
    background: "rgba(237, 119, 135, 0.15)",
    hover: "rgb(237, 119, 135)",
  },
  individual: {
    background: "rgba(49, 160, 178, 0.10)",
    hover: "#31A0B2",
  },
};

const useStyles = createStyles(() => ({
  ".rbc-calendar": {
    ".rbc-event.rbc-selected": {
      borderStyle: "hidden !important",
      backgroundColor: "transparent",
      opacity: 0.8,

      "&:focus": {
        outline: "none",
        border: "none",
      },
      "&:active": {
        outline: "none",
        border: "none",
      },
    },
    ".rbc-row-segment": {
      "&:focus": {
        outline: "none",
        border: "none",
      },
      "&:active": {
        border: "none",
      },
    },
    ".rbc-event": {
      backgroundColor: "transparent",
      "&:focus-visible": {
        outline: "none",
      },

      "&:focus": {
        outline: "none",
        border: "none",
      },
    },
    ".rbc-event selected": {
      "&:focus-visible": {
        outline: "none",
      },

      "&:focus": {
        outline: "none",
        border: "none",
      },
    },
    ".rbc-event.rbc-event-continues-later": {
      backgroundColor: "none",
      margin: "4px",
      color: "#062133",
    },
    ".rbc-event.rbc-event-continues-earlier": {
      backgroundColor: "none",
      margin: "4px",
      color: "#062133",
    },
    ".rbc-day-bg.rbc-today": {
      backgroundColor: "rgba(237, 119, 135, 0.10)",
    },

    ".rbc-event-content ": {
      color: "#062133",
      border: "none",
      borderRadius: "8px",
      "&:hover": {
        backgroundColor: "none",
        color: "#FFF",
      },
      "&:active": {
        outline: "none",
        border: "none",
      },
    },
  },
}));

const localizer = momentLocalizer(moment);

const calendarComponents = {
  event: (props: any) => {
    const backgroundColor =
      props.event.employees && props.event.employees.length > 0
        ? colors.individual.background
        : colors.companyWide.background;
    const hoverColor =
      props.event.employees && props.event.employees.length > 0
        ? colors.individual.hover
        : colors.companyWide.hover;

    return (
      <div
        className={
          props.event.employees && props.event.employees.length > 0
            ? "individual"
            : "companyWide"
        }
        style={
          {
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "14px",
            padding: "4px",
            backgroundColor,
            border: `1px solid ${hoverColor}`,
            borderRadius: "8px",
            paddingLeft: "10px",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            transition: "background-color 0.2s ease-in-out",
            ":hover": {
              backgroundColor: hoverColor,
            },
          } as any
        }
      >
        {`${props.title} `}
      </div>
    );
  },
};

const BlockedDatesTab: React.FC = () => {
  const {
    setIsCreateNew,
    isCreateNew,
    odpBlockedDates,
    groupedBlockedDates,
    updateSelectedBlockDate,
    isLoadingBlockedDates,
    currentBlockedDate,
  } = React.useContext(OdpBlockedDatesContext) as OdpBlockedDateContextType;

  const [activeBlockDateTab, setActiveBlockDateTab] = useState<
    "overview" | "calendar" | "edit" | "history"
  >("overview");
  const [blockedDates, setBlockedDates] = useState<IBlockedDateEvent[]>([]);
  const [groupBlocks, setGroupBlocks] = useState<IGroupedBlockedDateEvents>({
    futureEvents: [],
    currentEvents: [],
    pastEvents: [],
  });
  const [searchedEmployees, setSearchedEmployees] = React.useState<ItemProps[]>(
    []
  );
  const [employeeIdForHistory, setEmployeeIdForHistory] = useState<
    number | undefined
  >(undefined);
  const [employeeNameForHistory, setEmployeeNameForHistory] = useState<
    string | undefined
  >(undefined);

  const [openedEditBlockedDateDrawer, setOpenedEditBlockedDateDrawer] =
    useState<boolean>(false);

  const [
    openedConfirmationBlockedDatesModal,
    setOpenedConfirmationBlockedDatesModal,
  ] = useState<boolean>(false);

  const [isDeleteBlockedDatesModalOpened, setIsDeleteBlockedDatesModalOpened] =
    useState<boolean>(false);

  const { classes } = useStyles();
  useEffect(() => {
    setBlockedDates(odpBlockedDates);
  }, [odpBlockedDates]);

  useEffect(() => {
    setGroupBlocks(groupedBlockedDates);
  }, [groupedBlockedDates]);

  useEffect(() => {
    // Clear employee history state when switching away from history tab
    if (activeBlockDateTab !== "history") {
      setEmployeeIdForHistory(undefined);
      setEmployeeNameForHistory(undefined);
    }
  }, [activeBlockDateTab]);

  return isLoadingBlockedDates ? (
    <ProgressIndicator />
  ) : (
    <>
      <CreateNewBlockedDates isOpen={isCreateNew} setIsOpen={setIsCreateNew} />
      <EditBlockedDateDrawer
        openedEditBlockedDateDrawer={openedEditBlockedDateDrawer}
        setOpenedEditBlockedDateDrawer={setOpenedEditBlockedDateDrawer}
        setIsDeleteBlockedDatesModalOpened={setIsDeleteBlockedDatesModalOpened}
      />
      <DeleteBlockedDatesModal
        isDeleteBlockedDatesModalOpened={isDeleteBlockedDatesModalOpened}
        setIsDeleteBlockedDatesModalOpened={setIsDeleteBlockedDatesModalOpened}
        setOpenedConfirmationBlockedDatesModal={
          setOpenedConfirmationBlockedDatesModal
        }
      />

      <ConfirmationBlockedDatesModal
        openedConfirmationBlockedDatesModal={
          openedConfirmationBlockedDatesModal
        }
        setOpenedConfirmationBlockedDatesModal={
          setOpenedConfirmationBlockedDatesModal
        }
      />
      {blockedDates?.length > 0 && (
        <div>
          {activeBlockDateTab === "edit" && (
            <EditIndividualBlockDateEmployees
              searchedEmployees={searchedEmployees}
              setSearchedEmployees={setSearchedEmployees}
              setActiveBlockDateTab={setActiveBlockDateTab}
              setEmployeeIdForHistory={(id, name) => {
                setEmployeeIdForHistory(id);
                setEmployeeNameForHistory(name);
              }}
            />
          )}
          {activeBlockDateTab === "history" && (
            <BlockedDatesHistoryLog
              setActiveBlockDateTab={setActiveBlockDateTab}
              blockTitle={currentBlockedDate?.title}
              employeeId={employeeIdForHistory}
              employeeName={employeeNameForHistory}
            />
          )}
          {activeBlockDateTab !== "edit" &&
            activeBlockDateTab !== "history" && (
              <>
                <SimpleGrid cols={6} mt={16}>
                  <Button
                    onClick={() => setActiveBlockDateTab("overview")}
                    variant={
                      activeBlockDateTab === "overview" ? "filled" : "outline"
                    }
                    className="font-normal border-none"
                  >
                    Overview
                  </Button>
                  <Button
                    onClick={() => setActiveBlockDateTab("calendar")}
                    variant={
                      activeBlockDateTab === "calendar" ? "filled" : "outline"
                    }
                    className="font-normal border-none"
                  >
                    Calendar
                  </Button>
                </SimpleGrid>
                <BlockDatesFilter
                  groupedBlockedDates={groupedBlockedDates}
                  odpBlockedDates={odpBlockedDates}
                  setGroupBlocks={setGroupBlocks}
                  searchedEmployees={searchedEmployees}
                  setSearchedEmployees={setSearchedEmployees}
                />
              </>
            )}
          {activeBlockDateTab === "overview" && (
            <BlockedDatesOverView
              groupedBlockedDates={groupBlocks}
              setOpenedEditBlockedDateDrawer={setOpenedEditBlockedDateDrawer}
              updateSelectedBlockDate={updateSelectedBlockDate}
              setActiveBlockDateTab={setActiveBlockDateTab}
            />
          )}
          {activeBlockDateTab === "calendar" && (
            <Calendar
              timeslots={24}
              showAllEvents
              components={calendarComponents}
              formats={{
                dayHeaderFormat: (date) => moment(date).format("DD MMMM YYYY"),
              }}
              className={classes[".rbc-calendar"]}
              views={["week", "month"]}
              localizer={localizer}
              startAccessor="start"
              endAccessor="end"
              style={{
                margin: "10px",
                height: "75vh",
              }}
              events={[
                ...splitRecurringEvents(groupBlocks.pastEvents).map(
                  (event) => ({
                    ...event,
                    title:
                      event.employees && event.employees.length > 0
                        ? `${event.title} - ${event.employees.length} Employee(s) - Individual Block`
                        : `${event.title} - All Employees - Company Block`,
                  })
                ),
                ...splitRecurringEvents(groupBlocks.currentEvents).map(
                  (event) => ({
                    ...event,
                    title:
                      event.employees && event.employees.length > 0
                        ? `${event.title} - ${event.employees.length} Employee(s) - Individual Block`
                        : `${event.title} - All Employees - Company Block`,
                  })
                ),
                ...splitRecurringEvents(groupBlocks.futureEvents).map(
                  (event) => ({
                    ...event,
                    title:
                      event.employees && event.employees.length > 0
                        ? `${event.title} - ${event.employees.length} Employee(s) - Individual Block`
                        : `${event.title} - All Employees - Company Block`,
                  })
                ),
              ]}
              onSelectEvent={(event: any) => {
                updateSelectedBlockDate(event.id);
                setOpenedEditBlockedDateDrawer(true);
              }}
            />
          )}
        </div>
      )}

      {odpBlockedDates.length === 0 && <NoBlockedDates />}
    </>
  );
};

export default BlockedDatesTab;
